import { openDB, DBSchema, IDBPDatabase } from 'idb';
import SentryService from './SentryService';
import { StorageInitError, storageErrorTags } from './storage/classifyStorageError';

interface MyDB extends DBSchema {
  audioChunks: {
    key: string;
    value: Blob;
  };
}

class IndexedDBManager {
  private static instance: IndexedDBManager;
  private db: IDBPDatabase<MyDB> | null = null;
  private dbInitPromise: Promise<void> | null = null;
  private storagePersisted: boolean = false;
  private persistenceRequestPromise: Promise<boolean> | null = null;

  private constructor() {}

  public static getInstance(): IndexedDBManager {
    if (!IndexedDBManager.instance) {
      IndexedDBManager.instance = new IndexedDBManager();
    }
    return IndexedDBManager.instance;
  }

  // Make sure we only initialize once, even if called multiple times
  public async initDB(): Promise<void> {
    if (this.db) return; // Already initialized

    if (this.dbInitPromise) {
      // Another call is already initializing the DB, wait for it
      return this.dbInitPromise;
    }

    // Start the initialization process
    this.dbInitPromise = this._initDBInternal();
    try {
      await this.dbInitPromise;

      // After DB is initialized, check/request persistent storage
      await this.isStoragePersisted();
    } finally {
      this.dbInitPromise = null;
    }
  }

  private async _initDBInternal(): Promise<void> {
    try {
      const startTime = performance.now();

      // First try to open the DB normally
      try {
        this.db = await openDB<MyDB>('SmartScribeAudioChunks', 1, {
          upgrade(db) {
            if (!db.objectStoreNames.contains('audioChunks')) {
              db.createObjectStore('audioChunks');
            }
          },
        });

        // Verify the object store exists by trying a small read operation
        const tx = this.db.transaction('audioChunks', 'readonly');
        await tx.objectStore('audioChunks').count();
        await tx.done;
      } catch (error) {
        // If there was an error and we have a DB instance, close it
        if (this.db) {
          this.db.close();
          this.db = null;
        }

        console.warn('Database verification failed, trying recovery:', error);
        SentryService.addBreadcrumb({
          category: 'storage.indexeddb',
          message: 'Database verification failed, attempting recovery',
          level: 'warning',
          data: { error: String(error) },
        });

        // Try to recreate the database with a fresh version
        this.db = await openDB<MyDB>('SmartScribeAudioChunks', 1, {
          upgrade(db) {
            // Always create the store, even if it might already exist
            if (!db.objectStoreNames.contains('audioChunks')) {
              db.createObjectStore('audioChunks');
            }
          },
        });
      }

      const duration = performance.now() - startTime;

      SentryService.addBreadcrumb({
        category: 'storage.indexeddb',
        message: 'IndexedDB initialized',
        level: 'info',
        data: { durationMs: duration },
      });
    } catch (error) {
      // The browser throws a bare DOMException here (e.g. `UnknownError:
      // Internal error.`) with no JS stack, and re-throwing a generic
      // `new Error('Failed to initialize IndexedDB')` discards even the
      // original name. Wrap it so Sentry gets a real stack trace, keeps the
      // underlying error as `cause`, and tags the specific failure mode
      // (quota vs corrupted vs partitioned storage vs internal I/O error).
      const wrapped = new StorageInitError('IndexedDB init', error);
      SentryService.captureException(wrapped, {
        level: 'error',
        tags: { component: 'IndexedDBManager', ...storageErrorTags(wrapped) },
        extra: { method: 'initDB' },
      });
      throw wrapped;
    }
  }

  // Ensure DB is initialized and connection is valid before any operation
  private async ensureDBInitialized(): Promise<IDBPDatabase<MyDB>> {
    if (!this.db) {
      await this.initDB();
      if (!this.db) {
        throw new Error('Failed to initialize IndexedDB');
      }
    }

    // Check if the database connection is still valid
    try {
      // Try a simple operation to verify the connection is alive
      await this.db.transaction('audioChunks', 'readonly').done;
    } catch (error) {
      // If the connection is invalid, try to reinitialize
      if (
        error instanceof DOMException &&
        (error.name === 'InvalidStateError' || error.name === 'TransactionInactiveError')
      ) {
        SentryService.addBreadcrumb({
          category: 'storage.indexeddb',
          message: 'Database connection invalid, reinitializing',
          level: 'warning',
          data: { error: error.name, message: error.message },
        });

        this.db = null; // Clear the invalid connection
        await this.initDB();
        if (!this.db) {
          throw new Error('Failed to reinitialize IndexedDB after connection error');
        }
      } else {
        throw error;
      }
    }

    return this.db;
  }

  // Helper method to safely create transactions with retry logic
  private async safeTransaction(
    mode: 'readonly' | 'readwrite',
    retries: number = 1,
  ): Promise<{ db: IDBPDatabase<MyDB>; tx: any }> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const db = await this.ensureDBInitialized();

        // Check if object store exists before creating transaction
        if (!db.objectStoreNames.contains('audioChunks')) {
          SentryService.addBreadcrumb({
            category: 'storage.indexeddb',
            message: "Object store 'audioChunks' does not exist",
            level: 'warning',
            data: { availableStores: Array.from(db.objectStoreNames) },
          });
          throw new Error("Object store 'audioChunks' does not exist");
        }

        const tx = db.transaction('audioChunks', mode);
        return { db, tx };
      } catch (error) {
        const isLastAttempt = attempt === retries;

        if (
          error instanceof DOMException &&
          (error.name === 'InvalidStateError' ||
            error.name === 'TransactionInactiveError' ||
            error.message.includes('database connection is closing'))
        ) {
          SentryService.addBreadcrumb({
            category: 'storage.indexeddb',
            message: `Transaction creation failed (attempt ${attempt + 1}/${retries + 1})`,
            level: isLastAttempt ? 'error' : 'warning',
            data: {
              error: error.name,
              message: error.message,
              attempt: attempt + 1,
              willRetry: !isLastAttempt,
            },
          });

          if (!isLastAttempt) {
            // Clear the database connection and wait a bit before retrying
            this.db = null;
            await new Promise(resolve => setTimeout(resolve, 100 * (attempt + 1))); // Progressive delay
            continue;
          }
        }

        throw error;
      }
    }

    throw new Error('Failed to create transaction after all retry attempts');
  }

  public async writeBlob(blob: Blob, id: string): Promise<void> {
    const span = SentryService.startInactiveSpan('storage.write', `Write blob ${id}`);
    span?.setAttribute('blobId', id);
    span?.setAttribute('blobSize', blob.size);
    span?.setAttribute('blobType', blob.type);

    const startTime = performance.now();

    try {
      const { db, tx } = await this.safeTransaction('readwrite');

      await tx.store.put(blob, id);
      await tx.done;

      const duration = performance.now() - startTime;
      span?.setAttribute('duration', duration);
      span?.setStatus({ code: 1 });

      SentryService.setMeasurement(`blob_write_${id.substring(0, 8)}`, duration, 'millisecond');
      SentryService.setMeasurement(`blob_size_${id.substring(0, 8)}`, blob.size, 'byte');

      SentryService.addBreadcrumb({
        category: 'storage.indexeddb',
        message: `Blob ${id} written to IndexedDB`,
        level: 'info',
        data: { size: blob.size, durationMs: duration },
      });
    } catch (error) {
      span?.setStatus({ code: 2 });
      span?.setAttribute('error', error instanceof Error ? error.message : String(error));

      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.error('Storage quota exceeded.');

        SentryService.captureException(error, {
          level: 'error',
          tags: {
            component: 'IndexedDBManager',
            error_type: 'quota_exceeded',
          },
          extra: {
            method: 'writeBlob',
            blobSize: blob.size,
            blobId: id,
          },
        });

        // We should attempt to get storage usage stats for diagnostics
        try {
          if ('storage' in navigator && 'estimate' in navigator.storage) {
            navigator.storage.estimate().then(estimate => {
              const usageDetails = {
                usage: estimate.usage,
                quota: estimate.quota,
                usagePercentage:
                  estimate.usage && estimate.quota ? (estimate.usage / estimate.quota) * 100 : null,
              };

              SentryService.setExtra('storageEstimate', usageDetails);
            });
          }
        } catch (e) {
          // Ignore errors from the diagnostic code
        }
      } else {
        SentryService.captureException(error, {
          level: 'error',
          tags: { component: 'IndexedDBManager' },
          extra: {
            method: 'writeBlob',
            blobSize: blob.size,
            blobId: id,
          },
        });
      }

      throw error;
    } finally {
      span?.end();
    }
  }

  public async readBlob(id: string): Promise<Blob | undefined> {
    const span = SentryService.startInactiveSpan('storage.read', `Read blob ${id}`);
    span?.setAttribute('blobId', id);

    const startTime = performance.now();

    try {
      const { db, tx } = await this.safeTransaction('readonly');

      const blob = await tx.store.get(id);
      const duration = performance.now() - startTime;

      if (blob) {
        span?.setAttribute('blobFound', true);
        span?.setAttribute('blobSize', blob.size);
        span?.setAttribute('blobType', blob.type);
        span?.setAttribute('duration', duration);
        span?.setStatus({ code: 1 });

        SentryService.setMeasurement(`blob_read_${id.substring(0, 8)}`, duration, 'millisecond');

        SentryService.addBreadcrumb({
          category: 'storage.indexeddb',
          message: `Blob ${id} read from IndexedDB`,
          level: 'info',
          data: { size: blob.size, durationMs: duration },
        });
      } else {
        span?.setAttribute('blobFound', false);
        span?.setStatus({ code: 2 });

        SentryService.addBreadcrumb({
          category: 'storage.indexeddb',
          message: `Blob ${id} not found in IndexedDB`,
          level: 'warning',
        });
      }

      return blob;
    } catch (error) {
      span?.setStatus({ code: 2 });
      span?.setAttribute('error', error instanceof Error ? error.message : String(error));

      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'IndexedDBManager' },
        extra: {
          method: 'readBlob',
          blobId: id,
        },
      });

      throw error;
    } finally {
      span?.end();
    }
  }

  public async getAllKeys(): Promise<string[]> {
    try {
      const { db, tx } = await this.safeTransaction('readonly');
      return await tx.store.getAllKeys();
    } catch (error) {
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'IndexedDBManager' },
        extra: { method: 'getAllKeys' },
      });
      throw error;
    }
  }

  public async deleteBlob(id: string): Promise<void> {
    const span = SentryService.startInactiveSpan('storage.delete', `Delete blob ${id}`);
    span?.setAttribute('blobId', id);

    try {
      const { tx } = await this.safeTransaction('readwrite');

      await tx.store.delete(id);
      await tx.done;

      SentryService.addBreadcrumb({
        category: 'storage.indexeddb',
        message: `Blob ${id} deleted from IndexedDB`,
        level: 'info',
      });

      span?.setStatus({ code: 1 });
    } catch (error) {
      span?.setStatus({ code: 2 });
      span?.setAttribute('error', error instanceof Error ? error.message : String(error));

      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'IndexedDBManager' },
        extra: {
          method: 'deleteBlob',
          blobId: id,
        },
      });

      throw error;
    } finally {
      span?.end();
    }
  }

  public async clearAll(): Promise<void> {
    const span = SentryService.startInactiveSpan('storage.clear', 'Clear all audio chunks');

    try {
      const { db, tx } = await this.safeTransaction('readwrite');

      const keyCount = await tx.store.count();
      span?.setAttribute('keyCount', keyCount);

      await tx.store.clear();
      await tx.done;

      SentryService.addBreadcrumb({
        category: 'storage.indexeddb',
        message: 'Cleared all blobs from IndexedDB',
        level: 'info',
        data: { clearedCount: keyCount },
      });

      span?.setStatus({ code: 1 });
    } catch (error) {
      span?.setStatus({ code: 2 });
      span?.setAttribute('error', error instanceof Error ? error.message : String(error));

      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'IndexedDBManager' },
        extra: { method: 'clearAll' },
      });

      throw error;
    } finally {
      span?.end();
    }
  }

  public async checkStorageSpace(
    minimumChunksRequired: number = 1,
  ): Promise<{ hasEnoughSpace: boolean; usageDetails: StorageEstimate }> {
    if (!navigator.storage || !navigator.storage.estimate) {
      console.warn('Storage API not supported in this browser');
      return { hasEnoughSpace: true, usageDetails: { usage: 0, quota: 0 } }; // Assume space is available if we can't check
    }

    try {
      // Make sure DB is initialized before checking storage
      await this.ensureDBInitialized();

      const estimate = await navigator.storage.estimate();

      // Check if estimate is available and valid
      if (estimate.usage === undefined || estimate.quota === undefined) {
        console.warn('Storage quota or usage information not available');
        return { hasEnoughSpace: true, usageDetails: { usage: 0, quota: 0 } }; // Assume space is available if we can't get details
      }

      // Calculate available space
      const availableSpace = estimate.quota - estimate.usage;

      // Estimate size needed for chunks based on recording settings
      // Assuming average chunk size of 5MB per minute of audio (conservative estimate) and 90 seconds per chunk
      const estimatedChunkSize = 5 * 1024 * 1024 * (90 / 60); // ~7.5MB per chunk
      const requiredSpace = estimatedChunkSize * minimumChunksRequired;

      const usageDetails = {
        usage: estimate.usage,
        quota: estimate.quota,
        availableSpace: availableSpace,
        usagePercentage: (estimate.usage / estimate.quota) * 100,
        requiredSpace: requiredSpace,
        estimatedChunkSize: estimatedChunkSize,
      };

      SentryService.addBreadcrumb({
        category: 'storage.indexeddb',
        message: 'IndexedDB storage check',
        level: 'info',
        data: usageDetails,
      });

      return {
        hasEnoughSpace: availableSpace >= requiredSpace,
        usageDetails: estimate,
      };
    } catch (error) {
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'IndexedDBManager' },
        extra: { method: 'checkStorageSpace' },
      });

      console.error('Error checking storage space:', error);
      return { hasEnoughSpace: true, usageDetails: { usage: 0, quota: 0 } }; // Assume space is available if check fails
    }
  }

  /**
   * Requests persistent storage permission from the browser
   * This will ask the user for permission to use persistent storage
   * which helps prevent data loss in low storage situations
   * @returns Promise resolving to true if storage is now persistent, false otherwise
   */
  public async requestPersistentStorage(): Promise<boolean> {
    // If we already have a pending request, return that promise
    if (this.persistenceRequestPromise) {
      return this.persistenceRequestPromise;
    }

    try {
      // Only try if the API is available and we're in a secure context
      if (!navigator.storage || !navigator.storage.persist || !window.isSecureContext) {
        console.warn(
          'Storage persistence API not supported in this browser or not in a secure context (HTTPS required)',
        );
        return false;
      }

      // Make the request
      this.persistenceRequestPromise = navigator.storage.persist();
      const isPersisted = await this.persistenceRequestPromise;
      this.storagePersisted = isPersisted;

      // Add breadcrumb for monitoring
      SentryService.addBreadcrumb({
        category: 'storage.persistence',
        message: `Storage persistence ${isPersisted ? 'granted' : 'denied'}`,
        level: isPersisted ? 'info' : 'warning',
      });

      return isPersisted;
    } catch (error) {
      console.error('Error requesting persistent storage:', error);
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'IndexedDBManager' },
        extra: { method: 'requestPersistentStorage' },
      });
      return false;
    } finally {
      this.persistenceRequestPromise = null;
    }
  }

  /**
   * Checks if storage is currently set to persistent mode
   * @returns Promise resolving to true if storage is persistent, false otherwise
   */
  public async isStoragePersisted(): Promise<boolean> {
    try {
      // Check if the API is available and we're in a secure context
      if (!navigator.storage || !navigator.storage.persisted || !window.isSecureContext) {
        console.warn(
          'Storage persistence API not supported in this browser or not in a secure context (HTTPS required)',
        );
        return false;
      }

      // Check the current state
      const isPersisted = await navigator.storage.persisted();
      this.storagePersisted = isPersisted;

      if (!isPersisted) {
        console.info('Storage is not persistent, data may be cleared by the browser');
      }

      return isPersisted;
    } catch (error) {
      console.error('Error checking storage persistence:', error);
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'IndexedDBManager' },
        extra: { method: 'isStoragePersisted' },
      });
      return false;
    }
  }

  /**
   * Gets the current persistence status that was last checked
   * @returns True if storage is confirmed to be persistent, false otherwise
   */
  public getStoragePersistenceStatus(): boolean {
    return this.storagePersisted;
  }

  // Helper method to recreate database when it gets corrupted
  public async recreateDatabase(): Promise<void> {
    try {
      // Close existing connection if any
      if (this.db) {
        this.db.close();
        this.db = null;
      }

      // Delete the existing database
      await new Promise<void>((resolve, reject) => {
        const deleteReq = indexedDB.deleteDatabase('SmartScribeAudioChunks');
        deleteReq.onsuccess = () => resolve();
        deleteReq.onerror = () => reject(deleteReq.error);
        deleteReq.onblocked = () => {
          console.warn('Database deletion blocked, continuing anyway');
          resolve();
        };
      });

      SentryService.addBreadcrumb({
        category: 'storage.indexeddb',
        message: 'Recreated corrupted IndexedDB database',
        level: 'info',
      });

      // Reinitialize the database
      await this.initDB();
    } catch (error) {
      console.error('Error recreating database:', error);
      SentryService.captureException(error, {
        level: 'error',
        tags: { component: 'IndexedDBManager' },
        extra: { method: 'recreateDatabase' },
      });
      throw error;
    }
  }
}

export const indexedDBManager = IndexedDBManager.getInstance();
